import { useCallback } from "react";

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/**
 * Loads checkout.js exactly once. Resolves immediately if window.Razorpay is
 * already present, and attaches to an in-flight <script> if one exists rather
 * than injecting a duplicate.
 */
const loadScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load the payment library"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load the payment library"));
    document.body.appendChild(script);
  });

/**
 * Opens the Razorpay modal for a pre-created order.
 *
 * `amount` is the rupee total (the same number the summary displays); Razorpay
 * wants the smallest unit, so it is multiplied by 100 here.
 *
 * - onSuccess(response) fires with { razorpay_payment_id, razorpay_order_id,
 *   razorpay_signature } after the buyer pays.
 * - onFailure(error) fires on an explicit gateway payment failure.
 * - onDismiss() fires when the buyer closes the modal without paying (a cancel).
 */
export default function useRazorpayCheckout() {
  const open = useCallback(
    async ({ key, orderId, amount, currency, name, prefill, onSuccess, onFailure, onDismiss }) => {
      await loadScript();
      const rzp = new window.Razorpay({
        key,
        order_id: orderId,
        amount: Math.round(amount * 100),
        currency,
        name: name || "AiSensy",
        prefill: prefill || {},
        handler: (response) => onSuccess(response),
        // Dismiss = the buyer closed the modal (a cancel), NOT a payment failure.
        modal: { ondismiss: () => onDismiss && onDismiss() },
      });
      // Fires only on an actual gateway decline/error.
      rzp.on("payment.failed", (resp) => onFailure && onFailure(resp.error));
      rzp.open();
    },
    []
  );

  return { open };
}
